import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { adminApi } from '../../api/endpoints'
import { Button } from '../../components/ui/Button'
import { Card, CardBody, CardHeader } from '../../components/ui/Card'
import { ErrorState, LoadingState } from '../../components/ui/EmptyState'
import { Input } from '../../components/ui/Input'
import { useToast } from '../../components/ui/Toast'
import { PageHeader } from '../../layout/PageHeader'
import { formatDate } from '../../lib/utils'
import type { ConfiguracaoImportacaoPdf, ValoresConfiguracaoImportacaoPdf } from '../../types'

type Campo = keyof ValoresConfiguracaoImportacaoPdf

const CAMPOS: { campo: Campo; rotulo: string; unidade: string; inteiro: boolean; ajuda: string }[] = [
  { campo: 'tamanhoMaxMb', rotulo: 'Tamanho máximo do PDF', unidade: 'MB', inteiro: false, ajuda: 'Acima disso o envio é recusado.' },
  { campo: 'paginasMax', rotulo: 'Páginas máximas', unidade: 'páginas', inteiro: true, ajuda: 'PDFs com mais páginas são recusados.' },
  { campo: 'leiturasSimultaneas', rotulo: 'Leituras simultâneas', unidade: 'PDFs', inteiro: true, ajuda: 'Quantos PDFs são lidos ao mesmo tempo.' },
  { campo: 'capacidadeFila', rotulo: 'Capacidade da fila', unidade: 'PDFs', inteiro: true, ajuda: 'Com a fila cheia, novos envios são recusados até liberar espaço.' },
  { campo: 'leiturasPorHoraPorUsuario', rotulo: 'Leituras por hora por usuário', unidade: 'por hora', inteiro: true, ajuda: 'Envios aceitos por usuário na última hora.' },
  { campo: 'tempoMaxLeituraSeg', rotulo: 'Tempo máximo de leitura', unidade: 'segundos', inteiro: true, ajuda: 'Passou disso, a leitura é cancelada.' },
]

const num = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 })

/** Admin: limites anti-abuso da leitura de fatura em PDF. */
export function ImportacaoPdfConfigPage() {
  const { data, isLoading, isError, error } = useQuery({ queryKey: ['admin-importacao-pdf'], queryFn: adminApi.importacaoPdfConfiguracao })
  return (
    <>
      <PageHeader title="Importação de PDF" />
      {isLoading ? <Card><LoadingState /></Card> : isError || !data ? <Card><ErrorState message={(error as Error)?.message || 'Não foi possível carregar a configuração.'} /></Card> : <Formulario key={data.atualizadoEm ?? 'inicial'} config={data} />}
    </>
  )
}

function Formulario({ config }: { config: ConfiguracaoImportacaoPdf }) {
  const qc = useQueryClient()
  const toast = useToast()
  const [valores, setValores] = useState<Record<Campo, string>>(
    () => Object.fromEntries(CAMPOS.map((c) => [c.campo, String(config[c.campo])])) as Record<Campo, string>,
  )

  const paraNumero = (campo: Campo) => Number(valores[campo].trim().replace(',', '.'))

  const erros = Object.fromEntries(CAMPOS.map(({ campo, inteiro }) => {
    const f = config.faixas[campo]
    const n = paraNumero(campo)
    let msg: string | undefined
    if (valores[campo].trim() === '' || !Number.isFinite(n)) msg = 'Informe um número.'
    else if (inteiro && !Number.isInteger(n)) msg = 'Informe um número inteiro.'
    else if (n < f.minimo || n > f.maximo) msg = `Informe de ${num.format(f.minimo)} a ${num.format(f.maximo)}.`
    return [campo, msg]
  })) as Record<Campo, string | undefined>
  const valido = CAMPOS.every((c) => !erros[c.campo])

  const salvar = useMutation({
    mutationFn: () => adminApi.salvarImportacaoPdfConfiguracao({
      tamanhoMaxMb: paraNumero('tamanhoMaxMb'),
      paginasMax: paraNumero('paginasMax'),
      leiturasSimultaneas: paraNumero('leiturasSimultaneas'),
      capacidadeFila: paraNumero('capacidadeFila'),
      leiturasPorHoraPorUsuario: paraNumero('leiturasPorHoraPorUsuario'),
      tempoMaxLeituraSeg: paraNumero('tempoMaxLeituraSeg'),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-importacao-pdf'] }); toast.success('Configuração da importação de PDF salva.') },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <Card>
      <CardHeader title="Limites da leitura de fatura em PDF" icon="ri-file-pdf-2-line" />
      <CardBody className="space-y-5">
        <p className="text-13 text-muted">
          Travas globais contra abuso. Quem pode importar PDF e quantas vezes por mês é definido por plano, em Recursos por plano.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          {CAMPOS.map(({ campo, rotulo, unidade, inteiro, ajuda }) => {
            const f = config.faixas[campo]
            return (
              <div key={campo}>
                <Input
                  label={`${rotulo} (${unidade})`} type="number" inputMode={inteiro ? 'numeric' : 'decimal'}
                  min={f.minimo} max={f.maximo} step={inteiro ? 1 : 0.5}
                  value={valores[campo]} error={erros[campo]}
                  onChange={(e) => setValores((v) => ({ ...v, [campo]: e.target.value }))}
                />
                <p className="mt-1 text-2xs text-muted">
                  {ajuda} Padrão {num.format(f.padrao)}, de {num.format(f.minimo)} a {num.format(f.maximo)}.
                </p>
                {f.aplicaNaProximaSubida && (
                  <p className="mt-0.5 text-2xs text-warning"><i className="ri-information-line" aria-hidden /> Vale a partir da próxima subida da API.</p>
                )}
              </div>
            )
          })}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-2xs text-muted">{config.atualizadoEm ? `Última alteração em ${formatDate(config.atualizadoEm)}.` : 'Ainda não alterado (valendo os padrões).'}</p>
          <Button icon="ri-save-line" disabled={!valido} loading={salvar.isPending} onClick={() => salvar.mutate()}>Salvar</Button>
        </div>
      </CardBody>
    </Card>
  )
}
